import React, { useReducer, useState } from 'react';
import {
  Backdrop,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Box
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { newUserInputValidate } from '../../mobile/mobileComponents/mobileUserAccountLogin/userAccoountHook';
import { signUp } from '../../apiCalls/urls/urlFunction';
import { actionNotifier } from '../../components/tostify';
import ErrorMsg from '../../components/errorMsg/ErrorMsg';
import BasicBtn from '../../components/button/BasicBtn';

// ✅ Initial reducer state
const initialState = {
  formData: {
    name: '',
    email: '',
    mobile: '',
    password: '',
    referral: '',
    gender: '',
    city: '',
    termsAccepted: false,
  },
  errors: {
    nameError: '',
    emailError: '',
    mobileError: '',
    passwordError: '',
    referralError: '',
    genderError: '',
    cityError: '',
    termsAcceptedError: '',
  },
};

// ✅ Reducer function
function signupFormReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.field]: action.value,
        },
        errors: {
          ...state.errors,
          [`${action.field}Error`]: '', // Clear error on input change
        },
      };
    case 'SET_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          [`${action.field}Error`]: action.message,
        },
      };
    case 'RESET_FORM':
      return initialState;
    default:
      return state;
  }
}

const Signup = ({ handleUserForm, setIsUserAuthentic }) => {
  const [state, dispatch] = useReducer(signupFormReducer, initialState);
  const { formData, errors } = state;

  const [showPassword, setShowPassword] = useState(false);
  const [signUpLoading, setSignUpLoading] = useState(false);


  // ✅ Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('error');

  // ✅ Show alert utility
  const showAlert = (message, severity = 'error') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };



  // ✅ Handle input changes for all fields
  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    dispatch({ type: 'UPDATE_FIELD', field: name, value: fieldValue });
  };

  // ✅ Special handler for mobile/referral input to restrict to digits
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = value.replace(/\D/g, '');

    if (value !== sanitizedValue) {
      dispatch({ type: 'SET_ERROR', field: name, message: 'Only numeric characters are allowed.' });
    } else {
      dispatch({ type: 'SET_ERROR', field: name, message: '' });
    }

    handleChange({
      target: { name, value: sanitizedValue, type: 'text' },
    });
  };

  // ✅ Handle password visibility
  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleMouseDownPassword = (e) => e.preventDefault();

  // ✅ Send API request for signup
  const handleUserRegister = async (userDetails) => {
    const payload = {
      name: userDetails?.nameError?.title,
      email: userDetails?.emailError?.title,
      mobile: userDetails?.mobileError?.title,
      gender: userDetails?.genderError?.title,
      password: userDetails?.passwordError?.title,
      parent_id: userDetails?.referralError?.title,
      city: userDetails?.cityError?.title,
    };

    setSignUpLoading(true);

    try {
      const signupResponse = await signUp(payload);

      if (signupResponse?.data?.status) {
        actionNotifier.success("Otp Sent Successful", { position: "top-left", autoClose: 3000 });
        setIsUserAuthentic({
          mobile: payload?.mobile,
          status: signupResponse?.data?.status,
          isLoginForm: false
        });
        handleUserForm('USERREGISTERFORMFILLSUCCESSFUL');
        dispatch({ type: 'RESET_FORM' });
        return;
      } else {
        showAlert(signupResponse?.message || 'Signup failed');
      }
    } catch (error) {
      showAlert("Something went wrong. Please try again later.");
    } finally {
      setSignUpLoading(false);
    }
  };

  // ✅ Handle form submission and validate
  const handleSubmit = (e) => {
    e.preventDefault();

    const validation = newUserInputValidate(formData);

    if (validation?.isAnyError) {
      // Dispatch each error to reducer
      Object.entries(validation).forEach(([key, val]) => {
        if (val?.status) {
          const fieldName = key.replace('Error', '');
          dispatch({ type: 'SET_ERROR', field: fieldName, message: val.message });
        }
      });
      showAlert("Please correct the highlighted errors before submitting.");
    } else {
      handleUserRegister(validation);
    }
  };

  return (
    <Box component={"div"}>
      <ErrorMsg
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        message={alertMessage}
        severity={alertSeverity}
        height={1}
      />

      <FormControl component="form" method='post' onSubmit={handleSubmit} fullWidth>

        {/* Name */}
        <TextField
          fullWidth
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={!!errors.nameError}
          helperText={errors.nameError}
          margin="normal"
          required
        />

        {/* Mobile */}
        <TextField
          fullWidth
          label="Mobile Number"
          name="mobile"
          value={formData.mobile}
          onChange={handleInputChange}
          error={!!errors.mobileError}
          helperText={errors.mobileError}
          margin="normal"
          inputProps={{ maxLength: 10 }}
          required
        />

        {/* Email */}
        <TextField
          fullWidth
          label="Email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          error={!!errors.emailError}
          helperText={errors.emailError}
          margin="normal"
          required
        />

        {/* Password */}
        <TextField
          fullWidth
          label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange}
          error={!!errors.passwordError}
          helperText={errors.passwordError}
          margin="normal"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Referral Code */}
        <TextField
          fullWidth
          label="Referral Code (Optional)"
          name="referral"
          value={formData.referral}
          onChange={handleInputChange}
          error={!!errors.referralError}
          helperText={errors.referralError}
          margin="normal"
          inputProps={{ maxLength: 10 }}
        />

        {/* Gender */}
        <Typography variant='subtitle1' sx={{ mt: 2 }}>Select Gender:</Typography>
        <RadioGroup row name="gender" value={formData.gender} onChange={handleChange}>
          <FormControlLabel value="female" control={<Radio />} label="Female" />
          <FormControlLabel value="male" control={<Radio />} label="Male" />
          <FormControlLabel value="other" control={<Radio />} label="Other" />
        </RadioGroup>
        {errors.genderError && (
          <FormHelperText error>{errors.genderError}</FormHelperText>
        )}

        {/* City */}
        <TextField
          fullWidth
          label="City"
          name="city"
          value={formData.city}
          onChange={handleChange}
          error={!!errors.cityError}
          helperText={errors.cityError}
          margin="normal"
          required
        />

        {/* Terms & Conditions */}
        <FormControlLabel
          control={
            <Checkbox
              name="termsAccepted"
              checked={formData.termsAccepted}
              onChange={handleChange}
              color="primary"
              required
            />
          }
          label="I accept the terms and conditions"
        />
        {errors.termsAcceptedError && (
          <FormHelperText error>{errors.termsAcceptedError}</FormHelperText>
        )}

        <BasicBtn
          type="submit"
          name="Sign Up"
          color="secondary"
          smColor="primary"
          sx={{ mt: 3 }}
        />


      </FormControl>

      {/* Loading Spinner */}
      <Backdrop sx={{ color: '#fff', zIndex: 999 }} open={signUpLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default Signup;